import { DetailListItem, DetailsList, LineBottomSpacingOptions } from '@almbrand/detailslist';
import { Component } from 'components/Component';
import { usePageParam } from 'hooks';
import React, { useEffect, useState } from 'react';
import { isFieldHidden } from '../../../services/DependencyService';
import { replacePlaceholders } from '../../../services/PlaceholderService';
import styles from './DetailsListComponent.module.scss';

export interface DetailsListComponentProps {
	detailsList?: {
		items: {
			content: {
				primaryItem?: FieldsBlock;
				secondaryItem?: FieldsBlock;
				contentDependency?: ContentDependency;
				lineBottomSpacing: LineBottomSpacingOptions;
			};
		}[];
	};
	pageId?: string;
	boxed?: boolean;
	highlightDetails?: boolean;
}

export const DetailsListComponent: React.FC<DetailsListComponentProps> = ({ boxed = false, ...props }) => {
	const { getPageParam } = usePageParam();
	const [resultList, setResultList] = useState<Array<DetailListItem>>([]);

	// Generates a ReactNode for a given content block
	const getReactNode = (content: FieldsBlock): React.ReactNode => {
		replacePlaceholders(content);
		return (
			<Component
				key={content.contentId}
				{...props}
				// import content last to override the contentId etc.
				{...content}
			/>
		);
	};

	// Processes the details list and updates the result list
	const processDetailsList = () => {
		const res: Array<DetailListItem> = [];

		props.detailsList?.items?.forEach((listItem) => {
			if (isFieldHidden(listItem)) return;

			const { primaryItem, secondaryItem, lineBottomSpacing = 'default' } = listItem.content;

			const detailsListItem: DetailListItem = {
				primaryItem: primaryItem ? getReactNode({ ...primaryItem }) : undefined,
				secondaryItem: secondaryItem ? getReactNode({ ...secondaryItem }) : undefined,
				lineBottomSpacing,
			};

			if (detailsListItem.primaryItem || detailsListItem.secondaryItem) {
				res.push(detailsListItem);
			}
		});

		setResultList(res);
	};

	// Effect to process the details list when the page is in view
	useEffect(() => {
		if (getPageParam() === props.pageId) {
			processDetailsList();
		}
	}, [getPageParam()]);

	return (
		<DetailsList
			className={styles.DetailsListComponent}
			boxed={boxed}
			highlightDetails={props.highlightDetails}
			detailsList={resultList}
			backgroundColor={boxed ? 'color-primary-4' : undefined}
		/>
	);
};
